import './AdOpsTable.scss';
import './UnresolvedMappings.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdCheck, MdClose, MdExpandLess, MdExpandMore } from 'react-icons/md';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Skeleton, TableSkeleton } from '@Components/Skeleton';
import { BPMTable, Header } from '@Components/StickyTable';
import { Success500 } from '../../../utils/colors';
import { awaitJSON, S3SignedUrlFetch, StreamingUtilsLambdaFetch } from '../../../utils/fetch-utils';
import { conditionallyPluralizeWithCount } from '../../../utils/text-utils';
import { AdOpsToolProps } from '../adops-constants';
import { normalizeAdServer } from './streaming-mapping-utils';

const DERIVED_ID_PATTERN = '(BPM[A-Z0-9]+ID[0-9]+)';
const ISCI_PATTERN = '([A-Z]{2}[A-Za-z0-9]{2,}?ENH)';
const DISPLAY_PATTERN = '(.*?[0-9]{2,4}x[0-9]{2,4}.*)';
const MAPPING_S3_URI = 'bpm-spectrum/streaming_fetcher_missing_mappings/000.json';

interface UnresolvedMappingEntry {
  source: string;
  mappingType: string;
  parent?: string;
  name: string;
  id: string;
}

interface StagedMapping {
  entry: UnresolvedMappingEntry;
  bpmId: string;
}

interface StagedIgnore {
  entry: UnresolvedMappingEntry;
}

const rowKey = (entry: UnresolvedMappingEntry): string =>
  `${entry.source}|${entry.name}|${entry.id}`;

const patternMatch = (input: string, pattern: string): string => {
  const match = input.trim().match(new RegExp(pattern));
  return match ? match[1] : '';
};

export const UnresolvedMappings = ({ utils }: AdOpsToolProps) => {
  const { setError } = utils;
  const [tableData, setTableData] = useState<UnresolvedMappingEntry[]>([]);
  const [fetching, setFetching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bpmIdInputKey, setBpmIdInputKey] = useState<string | null>(null);
  const [stagedMappings, setStagedMappings] = useState<StagedMapping[]>([]);
  const [stagedIgnores, setStagedIgnores] = useState<StagedIgnore[]>([]);
  const [stagedPanelExpanded, setStagedPanelExpanded] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const bpmIdInputRef = useRef<HTMLInputElement>(null);

  const fetchMappingData = useCallback(async () => {
    setFetching(true);
    try {
      const res = await S3SignedUrlFetch(MAPPING_S3_URI);
      const parsedData = await awaitJSON<UnresolvedMappingEntry[]>(res);
      setTableData(parsedData);
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setFetching(false);
  }, [setError]);

  useEffect(() => {
    fetchMappingData();
  }, [fetchMappingData]);

  const stagedKeys = useMemo(
    () =>
      new Set([
        ...stagedMappings.map(m => rowKey(m.entry)),
        ...stagedIgnores.map(i => rowKey(i.entry))
      ]),
    [stagedMappings, stagedIgnores]
  );

  const displayData = useMemo(
    () => tableData.filter(e => !stagedKeys.has(rowKey(e))),
    [tableData, stagedKeys]
  );

  const confirmMap = useCallback((entry: UnresolvedMappingEntry) => {
    const value = bpmIdInputRef.current?.value?.trim();
    if (!value) {
      return;
    }
    setStagedMappings(prev => [...prev, { entry, bpmId: value }]);
    setBpmIdInputKey(null);
  }, []);

  const stageIgnore = useCallback((entry: UnresolvedMappingEntry) => {
    setStagedIgnores(prev => [...prev, { entry }]);
  }, []);

  const cancelMapInput = useCallback(() => {
    setBpmIdInputKey(null);
  }, []);

  const headers: Header[] = useMemo(
    () => [
      {
        label: 'Source',
        name: 'source',
        width: 150,
        sortAscending: true,
        sortPriority: 0
      },
      {
        label: 'Mapping Type',
        name: 'mappingType',
        width: 200,
        sortPriority: 1
      },
      {
        label: 'Placement / Creative Name',
        name: 'name',
        flex: 3,
        sortPriority: 2,
        renderer: (entry: UnresolvedMappingEntry) =>
          entry.parent ? `${entry.parent} — ${entry.name}` : entry.name
      },
      {
        label: 'ID',
        name: 'id',
        flex: 1
      },
      {
        label: 'Actions',
        name: 'actions',
        width: 320,
        nonInteractive: true,
        nonFilterable: true,
        renderer: (entry: UnresolvedMappingEntry) => {
          const key = rowKey(entry);
          if (bpmIdInputKey === key) {
            return (
              <div className='mapping-action-cell'>
                <Form.Control
                  ref={bpmIdInputRef}
                  autoFocus
                  className='mapping-bpm-id-input'
                  placeholder='Enter BPM ID...'
                  data-testid='bpm-id-input'
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      confirmMap(entry);
                    }
                    if (e.key === 'Escape') {
                      cancelMapInput();
                    }
                  }}
                />
                <IconButton
                  appearance={Appearance.FILLED}
                  design={Intent.SUCCESS}
                  size={Size.SM}
                  onClick={() => confirmMap(entry)}
                  data-testid='confirm-map'
                >
                  <MdCheck />
                </IconButton>
                <IconButton
                  appearance={Appearance.FILLED}
                  design={Intent.ERROR}
                  size={Size.SM}
                  onClick={cancelMapInput}
                  data-testid='cancel-map'
                >
                  <MdClose />
                </IconButton>
              </div>
            );
          }
          return (
            <div className='mapping-action-cell'>
              <Button
                appearance={Appearance.OUTLINED}
                size={Size.SM}
                onClick={() => setBpmIdInputKey(key)}
                data-testid={`map-${entry.id}`}
              >
                Map
              </Button>
              <Button
                appearance={Appearance.OUTLINED}
                design={Design.NEUTRAL}
                size={Size.SM}
                onClick={() => stageIgnore(entry)}
                data-testid={`ignore-${entry.id}`}
              >
                Ignore
              </Button>
            </div>
          );
        }
      }
    ],
    [bpmIdInputKey, confirmMap, stageIgnore, cancelMapInput]
  );

  const commit = useCallback(async () => {
    setSubmitting(true);
    try {
      const committedKeys = new Set<string>();
      let successfulMappings = 0;
      let successfulIgnores = 0;

      if (stagedMappings.length) {
        const derivedIdMappings: Record<
          string,
          { derivedId: string; partnerIds: string[]; adServer: string }
        > = {};
        const isciMappings: Record<string, string[]> = {};
        const failedParsing: string[] = [];

        for (const { entry, bpmId } of stagedMappings) {
          const derivedId = patternMatch(bpmId, DERIVED_ID_PATTERN);
          const isci = patternMatch(bpmId, ISCI_PATTERN);
          const displayId = patternMatch(bpmId, DISPLAY_PATTERN);
          const partnerId = entry.id;

          if (derivedId) {
            const adServer = normalizeAdServer(entry.source);
            if (!adServer) {
              failedParsing.push(`${entry.source} - ${bpmId}: ${partnerId}`);
              continue;
            }

            const key = `${derivedId} ${adServer}`;
            const existing = derivedIdMappings[key];
            derivedIdMappings[key] = {
              derivedId,
              partnerIds: [...(existing?.partnerIds || []), partnerId],
              adServer
            };
            committedKeys.add(rowKey(entry));
            successfulMappings++;
          } else if (isci) {
            isciMappings[isci] = [...(isciMappings[isci] || []), partnerId];
            committedKeys.add(rowKey(entry));
            successfulMappings++;
          } else if (displayId) {
            isciMappings[displayId] = [...(isciMappings[displayId] || []), partnerId];
            committedKeys.add(rowKey(entry));
            successfulMappings++;
          } else {
            failedParsing.push(`${entry.source} - ${bpmId}: ${partnerId}`);
          }
        }

        const mappingRes = await StreamingUtilsLambdaFetch('/streamingMappingAdder', {
          method: 'POST',
          body: { derivedIdMappings, isciMappings, failedMappings: failedParsing }
        });
        const { failedPartnerIds, ambiguousIsci, ambiguousDerivedId, ambiguousSimilarPlacements } =
          await awaitJSON<{
            failedPartnerIds: string[];
            ambiguousIsci: string[];
            ambiguousDerivedId: string[];
            ambiguousSimilarPlacements: string[];
          }>(mappingRes);
        const failedPartnerIdSet = new Set(failedPartnerIds);

        const failedMappings: UnresolvedMappingEntry[] = [];
        for (const { entry } of stagedMappings) {
          if (failedPartnerIdSet.has(entry.id)) {
            committedKeys.delete(rowKey(entry));
            successfulMappings--;
            failedMappings.push(entry);
          }
        }

        if (failedParsing.length) {
          await setError({
            message: `The following mappings could not be parsed: ${failedParsing.join(', ')}`,
            reportError: new Error('Failed parsing')
          });
        }
        if (failedMappings.length) {
          await setError({
            message: `The following entries could not be mapped due to ambiguous or missing records: ${failedMappings
              .map(e => rowKey(e))
              .join(', ')}`,
            reportError: new Error('Failed mappings')
          });
        }
        const ambiguousDetails = [
          ...ambiguousDerivedId,
          ...ambiguousSimilarPlacements,
          ...ambiguousIsci
        ];
        if (ambiguousDetails.length) {
          await setError({
            message: `The following entries need manual review before they can be mapped: ${ambiguousDetails.join('; ')}`,
            reportError: new Error('Ambiguous mappings')
          });
        }
      }

      if (stagedIgnores.length) {
        const lineItemIgnores: Record<string, string[]> = {};
        const isciIgnores: Record<string, string[]> = {};

        for (const { entry } of stagedIgnores) {
          const rawSource = entry.source.trim().toUpperCase();
          const source = rawSource === 'WMT' ? 'TTD' : rawSource;

          if (entry.mappingType === 'ISCI') {
            isciIgnores[source] = [...(isciIgnores[source] || []), entry.name];
          } else {
            lineItemIgnores[source] = [...(lineItemIgnores[source] || []), entry.name];
          }
          committedKeys.add(rowKey(entry));
          successfulIgnores++;
        }

        await StreamingUtilsLambdaFetch('/streamingIgnoreAdder', {
          method: 'POST',
          body: { lineItemIgnores, isciIgnores, failedIgnores: [] }
        });
      }

      setStagedMappings(prev => prev.filter(m => !committedKeys.has(rowKey(m.entry))));
      setStagedIgnores(prev => prev.filter(i => !committedKeys.has(rowKey(i.entry))));

      if (committedKeys.size > 0) {
        setTableData(prev => prev.filter(e => !committedKeys.has(rowKey(e))));
        const parts: string[] = [];
        if (successfulMappings > 0) {
          parts.push(conditionallyPluralizeWithCount(successfulMappings, 'mapping'));
        }
        if (successfulIgnores > 0) {
          parts.push(conditionallyPluralizeWithCount(successfulIgnores, 'ignore'));
        }
        setSuccessMessage(`${parts.join(' and ')} committed successfully.`);
        setShowSuccessDialog(true);
      }
    } catch (e) {
      setError({ message: e.message, reportError: e });
    }
    setSubmitting(false);
  }, [stagedMappings, stagedIgnores, setError]);

  const hasStagedChanges = stagedMappings.length > 0 || stagedIgnores.length > 0;

  return (
    <div
      className='table-container unresolved-mappings-container'
      data-testid='unresolved-mappings'
    >
      <div className='table-title'>Unresolved Streaming Mappings</div>
      {fetching ? (
        <Skeleton>
          <TableSkeleton />
        </Skeleton>
      ) : (
        <BPMTable
          className='ad-ops-table streaming-mapping-table'
          headers={headers}
          alternateColors
          data={displayData}
          filterBar
          rowHeight={72}
        />
      )}
      {hasStagedChanges && (
        <div className='staged-changes-panel' data-testid='staged-changes'>
          <div className='staged-changes-header'>
            <InteractiveDiv
              className='staged-changes-collapse-btn'
              onClick={() => setStagedPanelExpanded(prev => !prev)}
            >
              {stagedPanelExpanded ? <MdExpandLess /> : <MdExpandMore />}
              <span className='staged-changes-title'>Staged Changes</span>
              {!stagedPanelExpanded && (
                <span className='staged-changes-summary'>
                  Mappings: {stagedMappings.length} &nbsp;·&nbsp; Ignores: {stagedIgnores.length}
                </span>
              )}
            </InteractiveDiv>
            <Button
              size={Size.SM}
              loading={submitting}
              onClick={commit}
              data-testid='commit-staged'
            >
              Commit
            </Button>
          </div>
          {stagedPanelExpanded && (
            <div className='staged-changes-body'>
              {stagedMappings.length > 0 && (
                <div className='staged-section'>
                  <div className='staged-section-title'>Mappings ({stagedMappings.length})</div>
                  {stagedMappings.map(({ entry, bpmId }) => {
                    const key = rowKey(entry);
                    return (
                      <div key={key} className='staged-row'>
                        <span className='staged-row-label'>
                          <strong>{bpmId}</strong> → {entry.name} ({entry.id})
                        </span>
                        <IconButton
                          className='staged-row-remove'
                          appearance={Appearance.EMPTY}
                          onClick={() =>
                            setStagedMappings(prev => prev.filter(m => rowKey(m.entry) !== key))
                          }
                        >
                          <MdClose />
                        </IconButton>
                      </div>
                    );
                  })}
                </div>
              )}
              {stagedIgnores.length > 0 && (
                <div className='staged-section'>
                  <div className='staged-section-title'>Ignores ({stagedIgnores.length})</div>
                  {stagedIgnores.map(({ entry }) => {
                    const key = rowKey(entry);
                    return (
                      <div key={key} className='staged-row'>
                        <span className='staged-row-label'>
                          {entry.source} [{entry.mappingType}] — {entry.name} ({entry.id})
                        </span>
                        <IconButton
                          className='staged-row-remove'
                          appearance={Appearance.EMPTY}
                          onClick={() =>
                            setStagedIgnores(prev => prev.filter(i => rowKey(i.entry) !== key))
                          }
                        >
                          <MdClose />
                        </IconButton>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <AlertDialog
        show={showSuccessDialog}
        onHide={() => setShowSuccessDialog(false)}
        intent={Intent.SUCCESS}
        icon={<MdCheck color={Success500} size={75} />}
        title='Success!'
        body={successMessage}
        passthroughProps={{ 'data-testid': 'mappings-success' }}
      />
    </div>
  );
};
